import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Box,
  Alert,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Skeleton,
  Stack,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InboxIcon from '@mui/icons-material/Inbox';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useAuth } from '../context/AuthContext';
import TaskSummaryTable from '../components/TaskSummaryTable';
import { getTasksSummary } from '../services/clickupService';
import { classifyTaskStatus } from '../utils/taskStatus';

function KpiCard({ icon, label, value, accent, isLoading }) {
  const theme = useTheme();
  const accentColor = accent || theme.palette.primary.main;
  return (
    <Card
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: 2 },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: 4,
          height: '100%',
          bgcolor: accentColor,
        },
      }}
    >
      <CardContent sx={{ pl: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
              {label}
            </Typography>
            {isLoading ? (
              <Skeleton width={60} height={40} />
            ) : (
              <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2, mt: 0.5 }}>
                {value}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${accentColor}15`,
              color: accentColor,
            }}
          >
            {icon}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function computeKpis(clickupData) {
  if (!clickupData?.lists) {
    return { total: 0, requested: 0, forApproval: 0, inProgress: 0, done: 0 };
  }
  let total = 0;
  let requested = 0;
  let forApproval = 0;
  let inProgress = 0;
  let done = 0;
  for (const list of clickupData.lists) {
    total += list.totalTasks || 0;
    const counts = list.statusCounts || {};
    for (const [status, count] of Object.entries(counts)) {
      const c = Number(count) || 0;
      const statusType = list.statusTypes?.[status] || clickupData.allStatusTypes?.[status];
      const cls = classifyTaskStatus(status, statusType);
      if (cls === 'requested') requested += c;
      else if (cls === 'forApproval') forApproval += c;
      // "approved" se cuenta como en proceso (pendiente de generar orden)
      else if (cls === 'inProgress' || cls === 'pending' || cls === 'received' || cls === 'approved') inProgress += c;
      else if (cls === 'done') done += c;
    }
  }
  return { total, requested, forApproval, inProgress, done };
}

function SummaryPage() {
  const theme = useTheme();
  const { folderID, user } = useAuth();

  const [clickupData, setClickupData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showReadOnlyBanner, setShowReadOnlyBanner] = useState(false);
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);

  useEffect(() => {
    loadClickupData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderID, showCompletedTasks]);

  useEffect(() => {
    setShowReadOnlyBanner(user?.isReadOnly === true);
  }, [user]);

  const loadClickupData = async () => {
    if (!folderID) {
      setError('No se encontró el ID del folder. Por favor, vuelva a iniciar sesión.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const data = await getTasksSummary(folderID, { includeCompleted: showCompletedTasks });
      setClickupData(data);
    } catch (err) {
      console.error('Error cargando datos de ClickUp:', err);
      if (err.response) {
        setError(`Error ${err.response.status}: ${err.response.data?.message || err.response.statusText}`);
      } else if (err.request) {
        setError('No se recibió respuesta del servidor. Verifique su conexión a internet.');
      } else {
        setError(`Error al preparar la solicitud: ${err.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const kpis = useMemo(() => computeKpis(clickupData), [clickupData]);
  const kpiGridMd = showCompletedTasks ? 2.4 : 3;

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, margin: '0 auto', mt: { xs: 1, sm: 2 } }}>
      {showReadOnlyBanner && (
        <Alert
          severity="warning"
          icon={<VisibilityIcon />}
          onClose={() => setShowReadOnlyBanner(false)}
          sx={{ mb: 3, fontWeight: 500 }}
        >
          Has iniciado sesión con permisos de <strong>solo lectura</strong>. Las acciones de autorización están deshabilitadas.
        </Alert>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        sx={{ mb: 3, gap: 1 }}
      >
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            Resumen del Proyecto
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {clickupData?.folderName || 'Visión general de tareas y autorizaciones'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={showCompletedTasks}
                onChange={(event) => setShowCompletedTasks(event.target.checked)}
                color="secondary"
              />
            }
            label="Mostrar completadas"
          />
          <Tooltip title="Actualizar datos" arrow>
            <span>
              <IconButton
                onClick={loadClickupData}
                disabled={isLoading}
                sx={{
                  bgcolor: 'common.white',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                <RefreshIcon sx={{
                  animation: isLoading ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }} />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {!folderID && !isLoading && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          No se ha detectado un ID de folder válido. Por favor, vuelva a iniciar sesión.
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={loadClickupData}>
              Reintentar
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={kpiGridMd}>
          <KpiCard
            icon={<AssignmentIcon />}
            label="Total tareas"
            value={kpis.total}
            accent={theme.palette.primary.main}
            isLoading={isLoading}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={kpiGridMd}>
          <KpiCard
            icon={<HowToRegIcon />}
            label="Por aprobar"
            value={kpis.requested}
            accent={theme.palette.statuses?.requested?.main || theme.palette.warning.main}
            isLoading={isLoading}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={kpiGridMd}>
          <KpiCard
            icon={<HourglassTopIcon />}
            label="Por autorizar"
            value={kpis.forApproval}
            accent={theme.palette.secondary.main}
            isLoading={isLoading}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={kpiGridMd}>
          <KpiCard
            icon={<AutorenewIcon />}
            label="En proceso"
            value={kpis.inProgress}
            accent={theme.palette.info.main}
            isLoading={isLoading}
          />
        </Grid>
        {showCompletedTasks && (
          <Grid item xs={12} sm={6} md={kpiGridMd}>
            <KpiCard
              icon={<CheckCircleIcon />}
              label="Completadas"
              value={kpis.done}
              accent={theme.palette.success.main}
              isLoading={isLoading}
            />
          </Grid>
        )}
      </Grid>

      {isLoading && (
        <Card>
          <CardContent>
            <Skeleton variant="text" width="40%" height={32} />
            <Skeleton variant="rectangular" height={120} sx={{ mt: 2, borderRadius: 1 }} />
            <Skeleton variant="rectangular" height={120} sx={{ mt: 2, borderRadius: 1 }} />
            <Skeleton variant="rectangular" height={120} sx={{ mt: 2, borderRadius: 1 }} />
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && clickupData && (
        <TaskSummaryTable
          lists={clickupData.lists}
          statuses={clickupData.allStatuses}
          statusTypes={clickupData.allStatusTypes}
          showCompletedTasks={showCompletedTasks}
        />
      )}

      {!isLoading && !error && !clickupData && folderID && (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  bgcolor: 'grey.100',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <InboxIcon sx={{ fontSize: 36, color: 'text.secondary' }} />
              </Box>
              <Typography variant="h6" gutterBottom>
                No hay datos disponibles
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Aún no se han cargado tareas para este proyecto.
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<RefreshIcon />}
                onClick={loadClickupData}
              >
                Cargar datos
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {!isLoading && error && !clickupData && (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <ErrorOutlineIcon sx={{ fontSize: 56, color: 'error.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No se pudieron cargar los datos
              </Typography>
              <Button variant="contained" color="secondary" onClick={loadClickupData} sx={{ mt: 1 }}>
                Reintentar
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default SummaryPage;
